import type { CdSlot } from './CdRackGrid';

export type SlotStatus =
    | 'free'
    | 'reserved'
    | 'occupied'
    | 'low'
    | 'high'
    | 'full'
    | 'mixed'
    | 'overdue'
    | 'blocked';

export function slotStatus(slot: CdSlot): SlotStatus {
    if (slot.missing) return 'blocked';
    if (slot.overdue_count > 0) return 'overdue';
    if (!slot.occupied) return 'free';
    if (slot.slot_status) return slot.slot_status as SlotStatus;
    if (slot.occupancy_kind === 'cliente') return 'reserved';
    if (slot.occupancy_kind === 'mixed') return 'mixed';
    const pct = slot.fill_pct ?? 0;
    if (pct >= 91) return 'full';
    if (pct >= 61) return 'high';
    if (pct <= 30) return 'low';
    return 'occupied';
}

export function slotCardClasses(slot: CdSlot, highlighted: boolean): string {
    if (highlighted) {
        return [
            'border-amber-500 ring-1 ring-amber-400/60',
            'bg-amber-50 text-amber-900',
            'dark:bg-[#C9A84C]/15 dark:text-[#F0D06A] dark:border-[#C9A84C] dark:shadow-[0_0_10px_rgba(201,168,76,0.2)]',
        ].join(' ');
    }
    const st = slotStatus(slot);
    switch (st) {
        case 'blocked':
            return 'border-dashed border-gray-300 bg-gray-100 text-gray-400 dark:border-zinc-700 dark:bg-zinc-900/30 dark:text-zinc-700 opacity-50';
        case 'free':
            return 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800/50 dark:bg-emerald-900/20 dark:text-emerald-400';
        case 'reserved':
            return 'border-sky-300 bg-sky-50 text-sky-800 dark:border-sky-700/50 dark:bg-sky-900/20 dark:text-sky-400';
        case 'mixed':
            return 'border-violet-300 bg-violet-50 text-violet-800 dark:border-violet-700/50 dark:bg-violet-900/20 dark:text-violet-400';
        case 'overdue':
            return 'border-red-400 bg-red-50 text-red-800 dark:border-red-700/60 dark:bg-red-900/25 dark:text-red-400';
        case 'low':
            return 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-400';
        case 'high':
            return 'border-orange-400 bg-orange-50 text-orange-800 dark:border-orange-700/50 dark:bg-orange-900/20 dark:text-orange-400';
        case 'full':
            return 'border-rose-500 bg-rose-50 text-rose-800 dark:border-rose-700/60 dark:bg-rose-900/25 dark:text-rose-400';
        default:
            return 'border-gray-300 bg-gray-50 text-gray-700 dark:border-zinc-700/50 dark:bg-zinc-800/40 dark:text-zinc-300';
    }
}

/** Cores sólidas */
export function slotCardClassesSolid(slot: CdSlot, highlighted: boolean): string {
    if (highlighted) {
        return 'border-2 border-amber-500 bg-amber-100 text-amber-900 shadow-md dark:border-[#C9A84C] dark:bg-[#C9A84C]/25 dark:text-[#F0D06A]';
    }
    const st = slotStatus(slot);
    switch (st) {
        case 'blocked':
            return 'border border-gray-300 bg-gray-200 text-gray-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-600';
        case 'free':
            return 'border border-emerald-400 bg-emerald-100 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-800/60 dark:text-emerald-300';
        case 'reserved':
            return 'border border-sky-400 bg-sky-100 text-sky-800 dark:border-sky-700 dark:bg-sky-800/60 dark:text-sky-300';
        case 'mixed':
            return 'border border-violet-400 bg-violet-100 text-violet-800 dark:border-violet-700 dark:bg-violet-800/60 dark:text-violet-300';
        case 'overdue':
            return 'border border-red-500 bg-red-100 text-red-800 dark:border-red-700 dark:bg-red-800/60 dark:text-red-300';
        default:
            return 'border border-gray-300 bg-gray-100 text-gray-700 dark:border-zinc-600 dark:bg-zinc-700/60 dark:text-zinc-300';
    }
}

export function fillBarColor(pct: number): string {
    if (pct >= 91) return 'bg-rose-500';
    if (pct >= 61) return 'bg-orange-500';
    if (pct >= 31) return 'bg-amber-500';
    return 'bg-emerald-500';
}
